import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'

/**
 * Registers quick help options for the Service Detail Information tab.
 * Covers all action buttons and additional info sections.
 */
export const useServiceQuickHelp = () => {
    const { t } = useTranslation()

    const quickHelpOptions: QuickHelpOption[] = useMemo(
        () => [
            {
                id: 'add-note',
                label: 'Thêm ghi chú',
                description: 'Ghi nhận thông tin liên quan dịch vụ',
                buttonLabel: t('create-service-note'),
                tourTitle: 'Thêm Ghi Chú',
                tourDescription:
                    'Tạo ghi chú mới cho dịch vụ để ghi nhận thông tin quan trọng, cập nhật trạng thái, hoặc lưu ý cho các bộ phận khác.',
            },
            {
                id: 'edit-service',
                label: 'Chỉnh sửa thông tin dịch vụ',
                description: 'Cập nhật thông tin cơ bản',
                buttonLabel: t('service-edit'),
                tourTitle: 'Chỉnh Sửa Thông Tin',
                tourDescription:
                    'Chỉnh sửa thông tin dịch vụ: tên Portal/Domain/IP, Sales phụ trách, ngày bắt đầu, kết thúc, và các thông tin khác.',
            },
            {
                id: 'register-service',
                label: 'Đăng ký dịch vụ',
                description: 'Kích hoạt dịch vụ trên hệ thống',
                buttonLabel: t('register-service'),
                tourTitle: 'Đăng Ký Dịch Vụ',
                tourDescription:
                    'Gửi yêu cầu đăng ký/kích hoạt dịch vụ trên hệ thống provider (VCloud, VNIS, v.v.). Chỉ khả dụng với dịch vụ chưa đăng ký.',
            },
            {
                id: 'renew-service',
                label: 'Gia hạn dịch vụ',
                description: 'Gia hạn thời gian sử dụng',
                buttonLabel: t('renew-service'),
                tourTitle: 'Gia Hạn Dịch Vụ',
                tourDescription:
                    'Gửi yêu cầu gia hạn dịch vụ trên hệ thống provider. Áp dụng cho dịch vụ đã đăng ký và đang hoạt động.',
            },
            {
                id: 'create-invoice-renew',
                label: 'Tạo hoá đơn gia hạn',
                description: 'Xuất hoá đơn cho kỳ tiếp theo',
                buttonLabel: t('service-create-invoice-renew'),
                tourTitle: 'Tạo Hoá Đơn Gia Hạn',
                tourDescription:
                    'Tạo hoá đơn gia hạn cho dịch vụ dựa trên chu kỳ tiếp theo (continue_startdate → continue_enddate).',
            },
            {
                id: 'cancel-service',
                label: 'Huỷ/Off dịch vụ',
                description: 'Tắt hoặc huỷ dịch vụ',
                buttonLabel: t('cancel-service'),
                tourTitle: 'Huỷ/Off Dịch Vụ',
                tourDescription:
                    'Đưa dịch vụ vào trạng thái chờ huỷ (Pending Terminated). Có thể chọn lý do huỷ và ghi chú.',
            },
            {
                id: 'addons-service',
                label: 'Thêm dịch vụ addons',
                description: 'Thêm dịch vụ bổ sung',
                buttonLabel: t('addons-service'),
                tourTitle: 'Thêm Dịch Vụ Addons',
                tourDescription:
                    'Tạo dịch vụ addon/bổ sung cho dịch vụ chính. Addon sẽ được liên kết và quản lý cùng dịch vụ gốc.',
            },
            {
                id: 'edit-config',
                label: 'Chỉnh sửa cấu hình dịch vụ',
                description: 'Thay đổi cấu hình kỹ thuật',
                buttonLabel: t('edit-service-config'),
                tourTitle: 'Chỉnh Sửa Cấu Hình',
                tourDescription:
                    'Chỉnh sửa cấu hình kỹ thuật của dịch vụ: băng thông, dung lượng, số lượng resources, và các thông số khác.',
            },
            {
                id: 'show-more',
                label: 'Xem thêm chi tiết',
                description: 'Hiện thông tin bổ sung',
                buttonLabel: t('show-more-details'),
                tourTitle: 'Xem Thêm Chi Tiết',
                tourDescription:
                    'Hiển thị thông tin bổ sung: Loại dịch vụ, đơn vị tiền tệ, tỷ giá, thuế VAT, và người hưởng hoa hồng.',
            },
            {
                id: 'edit-vat',
                label: 'Chỉnh sửa thuế VAT',
                description: 'Thay đổi % thuế VAT',
                buttonLabel: t('edit-service-tax-percentage'),
                tourTitle: 'Chỉnh Sửa Thuế VAT',
                tourDescription:
                    'Thay đổi phần trăm thuế VAT của dịch vụ (0%, 5%, 8%, 10%). Cần mở "Xem thêm chi tiết" trước.',
            },
            {
                id: 'edit-service-type',
                label: 'Chỉnh sửa loại dịch vụ',
                description: 'Bán mới hoặc Upsale',
                buttonLabel: t('edit-service-type'),
                tourTitle: 'Chỉnh Sửa Loại Dịch Vụ',
                tourDescription:
                    'Thay đổi loại dịch vụ: Bán mới (new-selling) hoặc Upsale. Cần mở "Xem thêm chi tiết" trước.',
            },
            {
                id: 'edit-commission',
                label: 'Thay đổi người hưởng hoa hồng',
                description: 'Chọn Sales được hưởng commission',
                buttonLabel: t('edit-employee-sale-tracking'),
                tourTitle: 'Thay Đổi Người Hưởng Hoa Hồng',
                tourDescription:
                    'Chọn nhân viên Sales sẽ được tính hoa hồng từ dịch vụ này. Cần mở "Xem thêm chi tiết" trước.',
            },
            {
                id: 'edit-currency',
                label: 'Chỉnh sửa tiền tệ & tỷ giá',
                description: 'VND/USD và tỷ giá quy đổi',
                buttonLabel: t('edit-service-currency-exchange-rate'),
                tourTitle: 'Chỉnh Sửa Tiền Tệ & Tỷ Giá',
                tourDescription:
                    'Thay đổi đơn vị tiền tệ (VND, USD, EUR...) và tỷ giá quy đổi. Cần mở "Xem thêm chi tiết" trước.',
            },
        ],
        [t]
    )

    useQuickHelp(quickHelpOptions)
}
